import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import Popup from "reactjs-popup";
import { Badge } from '@opendate-app/setlist';
import DepositCard from './DepositCard';
import ManageFinanceItemsTable from './ManageFinanceItemsTable';
import PageSectionMenu from './PageSectionMenu';

const USDollar = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const popupContentStyle = {
  "background": "var(--sl-bg-primary)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "150px",
  "padding": "8px"
}

const NO_DEPOSITS = [];

const canAddNewFinanceItem = (financeItems) => {
  return !financeItems.some((fi) => !fi.id);
}

const sortedFinanceItems = (financeItems) => {
  return financeItems.sort(
    (a, b) => {
      return (
        ((a.id != null ? Infinity : 0) - (b.id != null ? Infinity : 0))
          ||  new Date(b.issued_on)) - (new Date(a.issued_on)
      );
    }
  )
}

const incomeFinanceItems = (financeItems) => {
  return sortedFinanceItems(financeItems.filter(financeItem => financeItem.is_income));
};

const expenseFinanceItems = (financeItems) => {
  return sortedFinanceItems(financeItems.filter(financeItem => !financeItem.is_income));
};

const ManageFinanceItems = ({
  csrfToken,
  team,
  confirm,
  currentUser,
  financeItems,
  financeItemsChanged,
  fetchNewFinanceItem,
  deleteFinanceItem,
  financeItemToEdit,
  financeItemToEditChanged,
  financeItemForForm,
  financeItemForFormChanged,
  financeCategories,
  financeCategoriesChanged,
  performers,
  createFinanceItem,
  updateFinanceItem,
  offers,
  offerChanged,
  updateDeposit,
  updateV3Deposit,
  rentalDeposits,
  v3Deposits = NO_DEPOSITS,
  confirmPageSection,
  fetchPermissionSet,
  updateConfirmPageSection,
  subtitle
}) => {
  if (!currentUser || !currentUser.can_manage_finance_items) {
    return null;
  }

  useEffect(() => {
    if(Object.keys(financeItemToEdit).length === 0) {
      financeItemsChanged(financeItems.filter(financeItem => financeItem.id));
    }
  }, [financeItemToEdit]);

  const [rentalDepositsState, setRentalDepositsState] = useState(rentalDeposits);
  const [v3DepositsState, setV3DepositsState] = useState(v3Deposits);

  useEffect(() => { setV3DepositsState(v3Deposits); }, [v3Deposits]);

  const allDeposits = [
    ...offers.flatMap((offer) => offer.budget.deposits),
    ...v3DepositsState,
    ...rentalDepositsState
  ];
  const unpaidDeposits = allDeposits.filter((deposit) => !deposit.paid);
  const depositColumnClass = allDeposits.length > 1 ? "col-12 col-lg-6 mb-2" : "col-12 mb-2";
  const unpaidTotal = unpaidDeposits.some((deposit) => deposit.amount == null)
    ? null
    : unpaidDeposits.reduce((sum, deposit) => sum + Number(deposit.amount), 0);

  return (
    <div>
      <div className={"card-body " + (financeItems.length > 0 ? "pb-0" : "")}>
        <div className={"row " + (financeItems.length > 0 ? "pb-0" : "pb-3")}>
          <div className="col">
            <h5 className="strong">
              Finances
              {confirmPageSection && confirmPageSection.permission_set_id ? (
                <i className="ml-2 far fa-lock small text-danger"></i>
              ) : null}
            </h5>
            {subtitle ? <p className="finance-section-subtitle">{subtitle}</p> : null}
          </div>
          <div className="col-xs-auto" style={{"paddingRight": "15px"}}>
            {currentUser.can_manage_finance_items && financeItems.length > 0 ? (
              <Popup arrow={false}
                      offsetY={5}
                      position="bottom right"
                      contentStyle={popupContentStyle}
                      onOpen={
                        (e) => {
                          e.preventDefault();
                        }
                      }
                      trigger={open => (
                        <a href="#"
                            className={"btn btn-external btn-sm " + (canAddNewFinanceItem(financeItems) ? "" : "disabled")}>
                          <strong>New Item</strong>
                        </a>
                      )} >
                {close => (
                  <div className='row text-left'>
                    <div className="col-12">
                      <ul className="list-group offer-inline-menu highlight-primary">
                        <li className="list-group-item">
                          <a href="#"
                              className="text-muted"
                              onClick={e => {
                                e.preventDefault();
                                fetchNewFinanceItem(team, true);
                                close();
                              }
                          }>
                            <span className="d-inline-block text-center" style={{width: "28px"}}>
                              <i className="fas fa-hand-holding-dollar"></i>
                            </span>
                            Income
                          </a>
                        </li>
                        <li className="list-group-item">
                          <a href="#"
                              className="text-muted"
                              onClick={e => {
                                e.preventDefault();
                                fetchNewFinanceItem(team, false);
                                close();
                              }
                          }>
                            <span className="d-inline-block text-center" style={{width: "28px"}}>
                              <i className="fas fa-receipt"></i>
                            </span>
                            Expense
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </Popup>
            ) : null }
            <PageSectionMenu csrfToken={csrfToken}
                              team={team}
                              confirm={confirm}
                              currentUser={currentUser}
                              confirmPageSection={confirmPageSection}
                              fetchPermissionSet={fetchPermissionSet}
                              updateConfirmPageSection={updateConfirmPageSection} />
          </div>
        </div>
        {financeItems.length === 0 ? (
          <div className="card no-border mb-0" style={{'background': 'var(--sl-bg-active)'}}>
            <div className="card-body">
              <div className="row">
                <div className="col-12 col-md-6 col-lg-4 order-md-2 text-left text-md-center d-flex justify-content-center align-items-center">
                  <i className="fal fa-dollar-sign"
                     style={{"fontSize": "120px", "color": "var(--sl-text-quaternary)"}}></i>
                </div>
                <div className="col-12 col-md-6 col-lg-8 order-md-1">
                  <h5>Keep track of income and expenses</h5>
                  <p className="text-muted">
                    Schedule and track deposits, artist payments, and other income to keep your show on budget.
                    Use Event Templates to created default income items.
                  </p>
                  <div className="d-flex">
                    {currentUser.can_manage_finance_items ? (
                      <Popup arrow={false}
                             offsetY={5}
                             position="bottom left"
                             contentStyle={popupContentStyle}
                             onOpen={
                               (e) => {
                                 e.preventDefault();
                               }
                             }
                             trigger={open => (

                                <a href="#"
                                   className={"btn btn-primary " + (canAddNewFinanceItem(financeItems) ? "" : "disabled")}>
                                  <strong>Add Income or Expense</strong>
                                </a>
                             )} >
                        {close => (
                          <div className='row text-left'>
                            <div className="col-12">
                              <ul className="list-group offer-inline-menu highlight-primary">
                                <li className="list-group-item">
                                  <a href="#"
                                     className="text-muted"
                                     onClick={e => {
                                       e.preventDefault();
                                       fetchNewFinanceItem(team, false);
                                       close();
                                     }
                                  }>
                                    <span className="d-inline-block text-center" style={{width: "28px"}}>
                                      <i className="fas fa-receipt"></i>
                                    </span>
                                    Add Expense
                                  </a>
                                </li>
                                <li className="list-group-item">
                                  <a href="#"
                                     className="text-muted"
                                     onClick={e => {
                                       e.preventDefault();
                                       fetchNewFinanceItem(team, true);
                                       close();
                                     }
                                  }>
                                    <span className="d-inline-block text-center" style={{width: "28px"}}>
                                      <i className="fas fa-hand-holding-dollar"></i>
                                    </span>
                                    Add Income
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </Popup>
                    ) : null}
                    <a href={`/teams/${team.id}/show_activity_settings`}
                      className="ml-2 btn text-primary"
                      style={{"boxShadow": "none"}}
                    >
                      <strong>Create Event Template</strong>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div className="row">
        {allDeposits.length > 0 ? (
          <div className="col-12 mb-4">
            <div className="mt-3 d-flex align-items-center" style={{"paddingLeft": "30px", "gap": "12px"}}>
              <h6 className="mb-0 strong" style={{"fontSize":"16px"}}>DEPOSITS</h6>
              {unpaidDeposits.length > 0 ? (
                <Badge color="orange">
                  {[`${unpaidDeposits.length} unpaid`, unpaidTotal == null ? null : USDollar.format(unpaidTotal)].filter(Boolean).join(" · ")}
                </Badge>
              ) : null}
            </div>
            <div className="mt-3 form-row" style={{"padding": "0 30px"}}>
              {offers.map((offer) => (
                offer.budget.deposits.map((deposit) => (
                  <DepositCard
                    key={`offer-deposit-${deposit.id}`}
                    name={offer.primary_performer ? offer.primary_performer?.artistable.name : offer.event_name}
                    deposit={deposit}
                    columnClass={depositColumnClass}
                    canManage={currentUser.can_manage_offers}
                    onTogglePaid={() => {
                      updateDeposit(csrfToken, team, deposit, !deposit.paid, (data) => {
                        offerChanged({
                          ...offer,
                          budget: {
                            ...offer.budget,
                            deposits: offer.budget.deposits.map((d) => (d.id === data.id ? data : d))
                          }
                        });
                      });
                    }}
                  />
                ))
              ))}
              {v3DepositsState.map((deposit) => (
                <DepositCard
                  key={`v3-deposit-${deposit.id}`}
                  name={deposit.artist_name || deposit.offer_name}
                  deposit={deposit}
                  columnClass={depositColumnClass}
                  canManage={currentUser.can_manage_offers}
                  onTogglePaid={() => {
                    const paid = !deposit.paid;

                    updateV3Deposit(csrfToken, team, deposit, paid, () => {
                      setV3DepositsState((prev) => prev.map((d) => (
                        d.id === deposit.id ? { ...d, paid, paid_at: paid ? new Date().toISOString() : null } : d
                      )));
                    });
                  }}
                />
              ))}
              {rentalDepositsState.map((deposit) => (
                <DepositCard
                  key={`rental-deposit-${deposit.id}`}
                  name={deposit.rental_invoice.title}
                  deposit={deposit}
                  columnClass={depositColumnClass}
                  canManage={currentUser.can_manage_rental_invoices}
                  onTogglePaid={() => {
                    const paid = !deposit.paid;

                    updateDeposit(csrfToken, team, deposit, paid, () => {
                      setRentalDepositsState((prev) => prev.map((d) => (
                        d.id === deposit.id ? { ...d, paid, paid_at: paid ? new Date().toISOString() : null } : d
                      )));
                    });
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}
        {financeItems.length > 0 ? (
          <>
            <ManageFinanceItemsTable
              title="INCOME"
              csrfToken={csrfToken}
              team={team}
              confirm={confirm}
              currentUser={currentUser}
              financeItems={incomeFinanceItems(financeItems)}
              deleteFinanceItem={deleteFinanceItem}
              financeItemToEdit={financeItemToEdit}
              financeItemToEditChanged={financeItemToEditChanged}
              financeItemForForm={financeItemForForm}
              financeItemForFormChanged={financeItemForFormChanged}
              financeCategories={financeCategories}
              financeCategoriesChanged={financeCategoriesChanged}
              performers={performers}
              createFinanceItem={createFinanceItem}
              updateFinanceItem={updateFinanceItem}
              offers={offers}
              offerChanged={offerChanged}
            />
            <ManageFinanceItemsTable
              title="EXPENSES"
              csrfToken={csrfToken}
              team={team}
              confirm={confirm}
              currentUser={currentUser}
              financeItems={expenseFinanceItems(financeItems)}
              deleteFinanceItem={deleteFinanceItem}
              financeItemToEdit={financeItemToEdit}
              financeItemToEditChanged={financeItemToEditChanged}
              financeItemForForm={financeItemForForm}
              financeItemForFormChanged={financeItemForFormChanged}
              financeCategories={financeCategories}
              financeCategoriesChanged={financeCategoriesChanged}
              performers={performers}
              createFinanceItem={createFinanceItem}
              updateFinanceItem={updateFinanceItem}
              offers={offers}
              offerChanged={offerChanged}
            />
          </>
        ) : null}
      </div>
    </div>
  );
};

ManageFinanceItems.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  financeItems: PropTypes.array,
  financeItemsChanged: PropTypes.func.isRequired,
  fetchNewFinanceItem: PropTypes.func.isRequired,
  deleteFinanceItem: PropTypes.func.isRequired,
  financeItemToEdit: PropTypes.object,
  financeItemToEditChanged: PropTypes.func.isRequired,
  financeItemForForm: PropTypes.object,
  financeItemForFormChanged: PropTypes.func.isRequired,
  financeCategories: PropTypes.array,
  financeCategoriesChanged: PropTypes.func.isRequired,
  performers: PropTypes.array,
  createFinanceItem: PropTypes.func.isRequired,
  updateFinanceItem: PropTypes.func.isRequired,
  offers: PropTypes.array,
  offerChanged: PropTypes.func.isRequired,
  updateDeposit: PropTypes.func.isRequired,
  updateV3Deposit: PropTypes.func.isRequired,
  rentalDeposits: PropTypes.array,
  v3Deposits: PropTypes.array,
  confirmPageSection: PropTypes.object,
  fetchPermissionSet: PropTypes.func.isRequired,
  updateConfirmPageSection: PropTypes.func.isRequired,
  subtitle: PropTypes.string
};

export default ManageFinanceItems;
